/**
 * PasteConvertPane — the "Paste a recipe" body of the recipe-creation modal.
 *
 * The first user-visible assistant surface (Track C, C-WP5). The user pastes a
 * plain-text recipe, names it, and converts it: the server (`assistant.paste
 * Convert`) structures it into the grammar, creates the recipe draft-only, and
 * seeds the converted body into the Draft. On success the modal closes and the
 * caller navigates to the editor with the Draft open.
 *
 * This pane owns the whole paste lifecycle so it is independently testable:
 *   - idle              — textarea + name + Convert
 *   - over-cap          — a quiet character counter turns to the removal colour;
 *                          Convert disabled; the text is NEVER truncated
 *   - in-flight         — quiet progress: a status line, a live elapsed-seconds
 *                          counter, and an expectation-setter; no spinner, no fake
 *                          progress bar (the dispatch is one non-streaming call)
 *   - total failure     — the pasted text is KEPT and a quiet failure line shows
 *                          (Rule 8; the recipe is not created server-side)
 *   - timeout           — the dispatch ran past its budget; a quiet RETRYABLE line
 *                          (the text is kept; Convert re-enables)
 *   - at-cap            — the shared "No operations left. Resets …" line inline;
 *                          Convert disabled; the rest of the modal is unaffected
 *   - opted-out         — the inline consent card (the assistant is available for
 *                          the environment but this user has not turned it on):
 *                          same consent copy as Settings, a "Turn on the
 *                          assistant" action and an equal "Not now". Declining
 *                          falls back to the quiet Settings pointer. Convert
 *                          stays disabled until the server confirms the opt-in
 *   - unavailable       — the global kill switch is off; a quiet line; Convert
 *                          disabled
 *   - not-configured    — the environment has no AI Gateway (a provisioning gap,
 *                          dev-only); a quiet line; Convert disabled; NO retry
 *                          advice (retrying can't fix a missing gateway)
 *   - seed-failed       — the recipe row WAS created but its body seed failed;
 *                          a distinct line + an "Open the recipe" action (no
 *                          retry — retrying would mint a duplicate)
 *
 * The title input is controlled by the modal (shared `title` state) so the name
 * persists across the Blank ⇄ Paste toggle.
 *
 * Layout (Rule 7): a scrolling content region + a PINNED footer — the Convert
 * CTA never scrolls away on a phone, and its divider runs full-bleed like every
 * other modal mode.
 *
 * Design refs:
 *   design.md §2 — one accent reserved for signal (the Convert CTA + the
 *                  textarea focus ring); hairlines, not shadows; quiet 200 ms
 *                  motion, no spinner theatrics.
 *   design.md §4 — Geist Mono for data only (the character counter), tabular-nums.
 *   branding.md §5, §5.2.1 — voice: precise, quiet; no exclamations, no banned
 *                  words, never "our AI"/"magic"/"smart" — "the assistant".
 *
 * Build plan ref: .plan/implementation/2026-07-10-track-c-ai-mcp-build-plan.md
 *   C-WP5 — Paste-to-syntax.
 */

import { useEffect, useRef, useState, type CSSProperties } from "react";
import { toast } from "sonner";
import { classifyMutateError } from "@proof/client-core/errors";
import { PASTE_MAX_CHARS } from "@proof/client-core/assistant";
import { aiOpsAtCapLine } from "../settings/aiOpsCopy.js";
import { setAssistantOptIn } from "../settings/assistantOptIn.js";
import { AssistantConsentCard } from "../assistant/AssistantConsentCard.js";
import {
  probePasteClaims,
  type PasteClaims,
  type PasteClaimsProbe,
  type PasteConvertFn,
} from "./pasteConvert.js";

// ── Copy (voice-reviewed; branding.md §5.2.1) ────────────────────────────────

/** Total-failure line — Rule 8, states what happened, text is kept. */
const FAILURE_LINE = "Couldn't read this as a recipe. The text is unchanged.";
/**
 * Timeout line — the model dispatch ran past its budget. TRANSIENT and retryable
 * (unlike FAILURE_LINE, which means "this isn't a recipe"): states what happened
 * and that another attempt is reasonable. The pasted text is kept (Rule 8).
 */
const TIMEOUT_LINE = "The conversion took too long. Try again.";
/** Defensive over-budget line (the cap normally prevents this reaching the server). */
const TOO_LARGE_LINE = "That's longer than the assistant can take. Trim it to convert.";
/**
 * Opted-out line — points to Settings, no pressure copy. Shown only AFTER the
 * user has declined the inline consent card ("Not now"): the card is the first
 * answer to an opted-out state, and this line is the quiet resting state once
 * the user has said no, so the disabled Convert still has an explanation.
 */
const OPTED_OUT_LINE = "The assistant is off. Turn it on in Settings → Assistant to convert.";
/** The consent card's first line — what the assistant would do on THIS surface. */
const CONSENT_CONTEXT_LINE = "The assistant converts pasted text into proof. syntax.";
/** Kill-switch-off line — honest, temporary, no pressure. */
const UNAVAILABLE_LINE = "The assistant is unavailable right now.";
/**
 * Not-configured line — a provisioning gap (no AI Gateway in this environment).
 * Distinct from UNAVAILABLE_LINE: no "right now" (retrying can't fix it) and no
 * retry advice. "Nothing was sent." reassures that no text left the client.
 */
const NOT_CONFIGURED_LINE =
  "The assistant isn't configured for this environment. Nothing was sent.";
/** Seed-failed line — the recipe exists; point the user to it (no retry). */
const SEED_FAILED_LINE =
  "The recipe was created, but the assistant couldn't add its body. Open it to write the recipe.";
/** In-flight status — states the action plainly (no spinner theatrics). */
const CONVERTING_STATUS = "Converting the recipe to an editable draft.";
/**
 * In-flight expectation-setter — frames the typical wait. The per-round dispatch
 * budget is 120s (PASTE_CONVERT_TIMEOUT_MS); a long recipe's single conversion pass
 * usually lands under two minutes. It is NOT a hard ceiling: the rare multi-round
 * repair loop can exceed two minutes, and the live elapsed counter would tick past
 * 2:00 in that case — so the copy says "usually … under" rather than "up to", which
 * would read as a bound the counter could visibly contradict.
 */
const CONVERTING_EXPECTATION = "Long recipes usually take under two minutes.";
/** Over-cap note beneath the counter. */
const OVER_CAP_NOTE = "That's longer than the assistant can take. Trim it to convert.";

// Accent focus ring on the textarea — a sanctioned accent site (design.md §2),
// matching the segmented controls' :focus-visible idiom. Inline styles can't
// express :focus-visible, so this ships as a one-off stylesheet.
const PASTE_PANE_CSS = `
  .proof-paste-textarea:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: -1px;
  }
`;

// ── Props ────────────────────────────────────────────────────────────────────

export interface PasteConvertPaneProps {
  /** Shared recipe title (owned by the modal). */
  title: string;
  /** Update the shared title. */
  onTitleChange: (value: string) => void;
  /** The paste-convert wire call. */
  convert: PasteConvertFn;
  /**
   * Called with the new recipe UUID + the typed title on success (modal closes
   * + navigates). The title lets the route seed its header title cache before
   * the recipe.list snapshot catches up (the converted row is not in the local
   * list yet — without it the workspace header falls back to "this recipe").
   */
  onConverted: (recipeUuid: string, title: string) => void;
  /** Called when the user cancels (closes the modal). */
  onCancel: () => void;
  /**
   * Pre-resolved claims for the inline at-cap / opted-out states. When provided
   * (tests), the pane skips the probe. When omitted the pane probes on mount.
   */
  claims?: PasteClaims | null;
  /** Injectable claims probe (tests); defaults to the real read. */
  probeClaims?: PasteClaimsProbe;
  /**
   * Persist the assistant opt-in from the inline consent card. Returns the
   * SERVER-confirmed value and MUST throw on failure (Rule 8) — the card
   * surfaces the error and the opted-out state is left untouched. Defaults to
   * the same `assistant.optIn.set` call Settings → Assistant uses; injectable
   * for tests.
   */
  setOptIn?: (enabled: boolean) => Promise<boolean>;
  /** Character cap; defaults to the shared {@link PASTE_MAX_CHARS}. */
  maxChars?: number;
  /** Shared name-input styling from the modal (keeps one look). */
  inputStyle: (hasError: boolean) => CSSProperties;
  /** Shared label styling from the modal. */
  labelStyle: CSSProperties;
  /** Max title length (mirrors the modal). */
  maxTitleLength: number;
  /**
   * Reports the pane's discard-guard state to the modal (F8): `busy` while a
   * convert is in flight, `dirty` while the textarea is non-empty. The modal
   * uses these to block accidental discard (overlay-click / Escape).
   */
  onGuardChange?: (busy: boolean, dirty: boolean) => void;
}

// ── Styles ───────────────────────────────────────────────────────────────────

// Root fills the modal panel below the header + toggle; content scrolls, footer
// pins (Rule 7 / footer parity with the other modal modes).
const rootStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  flex: "1 1 auto",
  minHeight: 0,
};

const scrollStyle: CSSProperties = {
  flex: "1 1 auto",
  minHeight: 0,
  overflowY: "auto",
  padding: "var(--space-4)",
  display: "flex",
  flexDirection: "column",
  gap: "var(--space-3)",
};

const fieldLabelStyle: CSSProperties = {
  marginBottom: "var(--space-1)",
};

const textareaStyle = (over: boolean): CSSProperties => ({
  width: "100%",
  boxSizing: "border-box",
  // Rule 7: adequate height, grows to a bounded max so the action row stays
  // reachable; the content region scrolls past that.
  minHeight: "clamp(140px, 30dvh, 320px)",
  resize: "vertical",
  border: over ? "1px solid var(--del)" : "1px solid var(--line-strong)",
  borderRadius: "var(--radius-button)",
  background: "transparent",
  padding: "var(--space-3)",
  fontFamily: "var(--font-sans)",
  // ≥16px so iOS Safari never zoom-traps on focus (Rule 7).
  fontSize: "16px",
  lineHeight: 1.5,
  color: "var(--text)",
  outline: "none",
  transition: "border-color var(--motion-state)",
});

const counterRowStyle: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  gap: "var(--space-2)",
  marginTop: "var(--space-1)",
};

const counterStyle = (over: boolean): CSSProperties => ({
  fontFamily: "var(--font-mono)",
  fontSize: "12px",
  fontVariantNumeric: "tabular-nums",
  color: over ? "var(--del)" : "var(--muted)",
});

const noteStyle: CSSProperties = {
  fontFamily: "var(--font-sans)",
  fontSize: "12px",
  color: "var(--del)",
};

const inlineStateStyle: CSSProperties = {
  fontFamily: "var(--font-sans)",
  fontSize: "12px",
  color: "var(--muted)",
};

const captionStyle: CSSProperties = {
  fontFamily: "var(--font-sans)",
  fontSize: "12px",
  color: "var(--muted)",
};

// In-flight progress block: a status line + a live elapsed counter on one row,
// then the expectation-setter beneath. No spinner, no fake bar (design.md §2 —
// the dispatch is one non-streaming call; the elapsed seconds are the only honest
// signal we have).
const progressStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--space-1)",
};

const progressRowStyle: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  gap: "var(--space-2)",
};

// Elapsed seconds — Geist Mono, tabular-nums (design.md §4: mono for data only).
const elapsedStyle: CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: "12px",
  fontVariantNumeric: "tabular-nums",
  color: "var(--muted)",
};

const failureStyle: CSSProperties = {
  fontFamily: "var(--font-sans)",
  fontSize: "12px",
  color: "var(--del)",
};

// Pinned footer: full-bleed divider + modal-matching padding (footer parity).
const footerStyle: CSSProperties = {
  display: "flex",
  flexDirection: "row",
  gap: "var(--space-2)",
  justifyContent: "flex-end",
  padding: "var(--space-3) var(--space-4)",
  borderTop: "1px solid var(--line)",
  flexShrink: 0,
};

const cancelButtonStyle: CSSProperties = {
  background: "none",
  border: "1px solid var(--line)",
  borderRadius: "var(--radius-button)",
  padding: "var(--space-2) var(--space-4)",
  fontFamily: "var(--font-sans)",
  fontSize: "13px",
  color: "var(--muted)",
  cursor: "pointer",
  minHeight: "var(--tap-target-min)",
  transition: "color var(--motion-state), border-color var(--motion-state)",
};

/**
 * Primary CTA. Disabled uses the shared neutral disabled tokens, NOT a washed
 * accent: a 50%-transparent accent still reads as accent, and design.md §2
 * reserves the accent for live signal — an inert control is not signal. The
 * token pair is the same one every other disabled CTA in the app uses
 * (SignInGate, AccountSection, reset-password) and reads 4.0:1, legible but
 * clearly below the enabled state.
 */
const primaryButtonStyle = (disabled: boolean): CSSProperties => ({
  background: disabled ? "var(--cta-disabled-bg)" : "var(--accent)",
  border: "none",
  borderRadius: "var(--radius-button)",
  padding: "var(--space-2) var(--space-4)",
  fontFamily: "var(--font-sans)",
  fontSize: "13px",
  color: disabled ? "var(--cta-disabled-fg)" : "var(--surface)",
  cursor: disabled ? "not-allowed" : "pointer",
  boxShadow: disabled ? "none" : "var(--shadow-cta)",
  minHeight: "var(--tap-target-min)",
  transition: "background var(--motion-state), color var(--motion-state)",
});

// ── Component ────────────────────────────────────────────────────────────────

/**
 * The default consent write — the SAME `assistant.optIn.set` call Settings →
 * Assistant uses, tagged with where the consent was captured (Art. 7(1)
 * demonstrability). It throws on failure, which is what the card needs.
 */
const defaultSetOptIn = (enabled: boolean): Promise<boolean> =>
  setAssistantOptIn(enabled, "paste_convert");

export function PasteConvertPane({
  title,
  onTitleChange,
  convert,
  onConverted,
  onCancel,
  claims: claimsProp,
  probeClaims = probePasteClaims,
  setOptIn = defaultSetOptIn,
  maxChars = PASTE_MAX_CHARS,
  inputStyle,
  labelStyle,
  maxTitleLength,
  onGuardChange,
}: PasteConvertPaneProps) {
  const [pastedText, setPastedText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  // Claims: `undefined` while probing, `null` when unavailable, else resolved.
  const [claims, setClaims] = useState<PasteClaims | null | undefined>(
    claimsProp === undefined ? undefined : claimsProp,
  );
  // Runtime blocks discovered on a convert error (the mount probe can't see
  // them): the per-user cap moved, or the global kill switch is off.
  const [forcedBlock, setForcedBlock] = useState<
    null | "at-cap" | "unavailable" | "not-configured"
  >(null);
  // Seed-failed: the recipe row exists but its body seed failed — open it, never
  // retry (a retry would mint a duplicate recipe).
  const [seedFailedUuid, setSeedFailedUuid] = useState<string | null>(null);
  // Elapsed seconds during a convert — the one honest progress signal (the
  // dispatch is a single non-streaming call, so there is no real intermediate
  // progress to show; a fake bar would be decoration, design.md §2).
  const [elapsedSec, setElapsedSec] = useState(0);
  // The user answered "Not now" to the inline consent ask. Scoped to THIS pane
  // instance and never persisted: the opted-out state is untouched, the card
  // comes back next time the pane is opened, and nothing nags in between.
  const [consentDeclined, setConsentDeclined] = useState(false);
  // Guards the post-consent claims refresh against a setState after unmount
  // (the modal can be closed while the opt-in save is in flight).
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Tick the elapsed counter once a second while a convert is in flight; reset it
  // when the flight ends and clear the interval on unmount (no leaked timer).
  useEffect(() => {
    if (!submitting) {
      setElapsedSec(0);
      return;
    }
    const started = Date.now();
    setElapsedSec(0);
    const id = setInterval(() => {
      setElapsedSec(Math.floor((Date.now() - started) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [submitting]);

  // Probe claims on mount when not supplied. Guard against setState-after-unmount.
  useEffect(() => {
    if (claimsProp !== undefined) return; // caller supplied claims (tests)
    let live = true;
    void probeClaims().then((c) => {
      if (live) setClaims(c);
    });
    return () => {
      live = false;
    };
    // Mount-only probe: probeClaims is a stable prop by contract and claimsProp
    // is read once. Intentionally no deps.
  }, []);

  const trimmedTitle = title.trim();
  const trimmedText = pastedText.trim();
  const overCap = pastedText.length > maxChars;
  const isPaid = claims?.isPaid ?? false;
  // The kill switch is read from the claims probe as well as from a failed
  // dispatch, so an environment with the assistant off shows the quiet line up
  // front instead of only after the user has spent a click on a call that is
  // guaranteed to be refused.
  const unavailable = forcedBlock === "unavailable" || claims?.enabled === false;
  const notConfigured = forcedBlock === "not-configured";
  const atCap =
    forcedBlock === "at-cap" || (claims != null && claims.aiOps.used >= claims.aiOps.cap);
  const optedOut = claims != null && claims.optedIn === false;
  const seedFailed = seedFailedUuid !== null;
  /**
   * The inline consent ask. Shown ONLY when the environment kill switch is on
   * and the user is opted out (plan: "Assistant inline consent at point of
   * use"): a consent card for a feature that cannot run would be a false offer,
   * so an env-OFF environment keeps the quiet unavailable line. Once the user
   * has declined, the pane falls back to the quiet Settings pointer.
   */
  const showConsentCard = optedOut && claims?.enabled === true && !consentDeclined;

  const canConvert =
    trimmedTitle.length > 0 &&
    trimmedText.length > 0 &&
    !overCap &&
    !submitting &&
    !atCap &&
    !optedOut &&
    !unavailable &&
    !notConfigured &&
    !seedFailed;

  // F8 — report discard-guard state (busy / dirty) to the modal.
  useEffect(() => {
    onGuardChange?.(submitting, pastedText.trim().length > 0);
  }, [submitting, pastedText, onGuardChange]);

  /**
   * The inline consent act. Throws on failure so {@link AssistantConsentCard}
   * shows its inline error and the opted-out state stays exactly where it was
   * (Rule 8 — the card never resolves on an unconfirmed save).
   *
   * When this pane owns the claims probe (production; tests may inject
   * `claims`) the consent does NOT resolve on the save alone: it re-probes and
   * applies the fresh read in ONE `setClaims`, so the card stays in its busy
   * state until then.
   *
   * That ordering is load-bearing, not tidiness. On the `assistant_not_enabled`
   * refusal path the local claims are `{ ...FALLBACK_CLAIMS, optedIn: false }`,
   * and FALLBACK_CLAIMS carries `aiOps: 0/0` — which satisfies `atCap`. Flipping
   * `optedIn` to true first would unmount the card and render "No operations
   * left." off a placeholder meter for as long as the probe takes. No state
   * derived from FALLBACK_CLAIMS is ever shown with `optedIn: true`.
   */
  async function handleConsent() {
    const confirmed = await setOptIn(true);
    if (confirmed !== true) {
      // The server did not confirm the opt-in. Treat it as a failed save rather
      // than displaying a consent the server does not hold.
      throw new Error("assistant_opt_in_not_confirmed");
    }
    if (!mountedRef.current) return;
    if (claimsProp !== undefined) {
      // The caller owns the claims (tests): there is nothing to re-probe, so
      // apply the server-confirmed value directly.
      setClaims((prev) => (prev != null ? { ...prev, optedIn: true } : prev));
      return;
    }
    // A fresh read WINS, including a failed one (`null` ⇒ unavailable). Keeping
    // the refusal-path fallback here would be worse than admitting the meter is
    // unknown. The pane's documented posture is that a probe failure must not
    // block Convert — the server cap stays authoritative.
    const fresh = await probeClaims();
    if (!mountedRef.current) return;
    setClaims(fresh);
  }

  async function handleConvert() {
    if (!canConvert) return;
    setSubmitting(true);
    setFailure(null);
    try {
      const { recipeUuid } = await convert({ title: trimmedTitle, pastedText });
      onConverted(recipeUuid, trimmedTitle);
      // Note: no setState after this — the modal unmounts on onConverted.
    } catch (err: unknown) {
      // Rule 8: the text is KEPT; a diagnostic is always logged.
      console.error({ event: "paste_convert_failed", err });
      const code = matchPasteError(err);
      if (code === "AI_OPS_EXCEEDED") {
        // Reuse the shared reset-aware toast mapping (W1 classify.ts).
        toast.error(classifyMutateError(err, { entity: "recipe" }).copy);
        setForcedBlock("at-cap");
      } else if (code === "assistant_not_enabled") {
        // Surface the opted-out inline state (points to Settings).
        setClaims((prev) =>
          prev != null ? { ...prev, optedIn: false } : { ...FALLBACK_CLAIMS, optedIn: false },
        );
      } else if (code === "assistant_unavailable") {
        setForcedBlock("unavailable");
      } else if (code === "ai_gateway_unconfigured") {
        // Provisioning gap (no AI Gateway in this env) — a persistent block, NOT
        // a retryable failure line: retrying can't add missing infra.
        setForcedBlock("not-configured");
      } else if (code === "paste_convert_seed_failed") {
        // The recipe exists — offer to open it; never retry (would duplicate).
        const uuid = readSeedFailedUuid(err);
        if (uuid) setSeedFailedUuid(uuid);
        else setFailure(FAILURE_LINE);
      } else if (code === "ai_timeout") {
        // Transient — the dispatch ran past its budget. Retryable: keep the text,
        // re-enable Convert (below), show a quiet retryable line (Rule 8).
        setFailure(TIMEOUT_LINE);
      } else if (code === "paste_too_large") {
        setFailure(TOO_LARGE_LINE);
      } else if (code === "paste_convert_failed") {
        setFailure(FAILURE_LINE);
      } else {
        // F9 — unknown / network / server: use the shared classifier copy
        // ("Couldn't reach the server." on a fetch failure), not the parse line.
        setFailure(classifyMutateError(err, { entity: "recipe" }).copy);
      }
      setSubmitting(false);
    }
  }

  const counterText = `${pastedText.length.toLocaleString()} / ${maxChars.toLocaleString()}`;

  return (
    <div style={rootStyle} data-testid="paste-convert-pane">
      <style>{PASTE_PANE_CSS}</style>
      <div style={scrollStyle}>
        {/* Shared name input (controlled by the modal). */}
        <div>
          <label htmlFor="proof-paste-title" style={{ ...labelStyle, ...fieldLabelStyle }}>
            Name
          </label>
          <input
            id="proof-paste-title"
            type="text"
            value={title}
            onChange={(e) => onTitleChange(e.target.value.slice(0, maxTitleLength))}
            style={inputStyle(false)}
            maxLength={maxTitleLength}
            autoComplete="off"
            placeholder="Pasta carbonara"
            disabled={submitting}
            data-testid="paste-convert-title"
          />
        </div>

        {/* Paste textarea. */}
        <div>
          <label htmlFor="proof-paste-textarea" style={{ ...labelStyle, ...fieldLabelStyle }}>
            Paste the recipe
          </label>
          <textarea
            id="proof-paste-textarea"
            className="proof-paste-textarea"
            value={pastedText}
            onChange={(e) => {
              setPastedText(e.target.value);
              if (failure) setFailure(null);
            }}
            style={textareaStyle(overCap)}
            placeholder="Paste a recipe in any format. The assistant turns it into an editable draft."
            disabled={submitting}
            aria-invalid={overCap}
            aria-describedby="proof-paste-counter"
            data-testid="paste-convert-textarea"
          />
          <div style={counterRowStyle}>
            {overCap ? (
              <span style={noteStyle} role="alert" data-testid="paste-convert-overcap">
                {OVER_CAP_NOTE}
              </span>
            ) : (
              <span />
            )}
            <span
              id="proof-paste-counter"
              style={counterStyle(overCap)}
              data-numeric
              data-testid="paste-convert-counter"
            >
              {counterText}
            </span>
          </div>
        </div>

        {/* Seed-failed region — the recipe exists; open it (no retry). */}
        {seedFailed && (
          <span style={inlineStateStyle} role="status" data-testid="paste-convert-seed-failed">
            {SEED_FAILED_LINE}
          </span>
        )}

        {/* Inline states — precedence: not-configured > unavailable > opted-out >
            at-cap. None blocks the Blank sub-mode. Hidden once the recipe exists.
            The opted-out slot carries the inline consent ask (the card), falling
            back to the quiet Settings pointer once the user has declined. */}
        {!seedFailed &&
          (notConfigured ? (
            <span style={inlineStateStyle} role="status" data-testid="paste-convert-not-configured">
              {NOT_CONFIGURED_LINE}
            </span>
          ) : unavailable ? (
            <span style={inlineStateStyle} role="status" data-testid="paste-convert-unavailable">
              {UNAVAILABLE_LINE}
            </span>
          ) : showConsentCard ? (
            /* `optIn` carries the SERVER-confirmed value derived from claims
               (`null` when the claims read is unavailable), not a hard-coded
               false — the card's own display rules then operate on real state
               instead of on an assumption this branch happens to guarantee.
               `showConsentCard` remains the outer gate. */
            <AssistantConsentCard
              context={CONSENT_CONTEXT_LINE}
              optIn={claims == null ? null : claims.optedIn}
              onConsent={handleConsent}
              onDecline={() => setConsentDeclined(true)}
            />
          ) : optedOut ? (
            <span style={inlineStateStyle} role="status" data-testid="paste-convert-opted-out">
              {OPTED_OUT_LINE}
            </span>
          ) : atCap ? (
            <span style={inlineStateStyle} role="status" data-testid="paste-convert-at-cap">
              {aiOpsAtCapLine(isPaid)}
            </span>
          ) : null)}

        {/* In-flight progress: status + live elapsed counter, then the expectation.
            role="status" announces the static lines once; the ticking counter is
            aria-hidden so a screen reader is not re-interrupted every second. */}
        {submitting && (
          <div style={progressStyle} role="status" data-testid="paste-convert-progress">
            <div style={progressRowStyle}>
              <span style={captionStyle}>{CONVERTING_STATUS}</span>
              <span
                style={elapsedStyle}
                data-numeric
                data-testid="paste-convert-elapsed"
                aria-hidden="true"
              >
                {elapsedSec}s
              </span>
            </div>
            <span style={captionStyle}>{CONVERTING_EXPECTATION}</span>
          </div>
        )}

        {/* Total-failure line (text kept). */}
        {failure !== null && (
          <span style={failureStyle} role="alert" data-testid="paste-convert-failure">
            {failure}
          </span>
        )}
      </div>

      {/* Pinned action row (footer parity). */}
      <div style={footerStyle}>
        <button
          type="button"
          style={cancelButtonStyle}
          onClick={onCancel}
          disabled={submitting}
          data-testid="paste-convert-cancel"
        >
          Cancel
        </button>
        {seedFailed ? (
          <button
            type="button"
            style={primaryButtonStyle(false)}
            onClick={() => onConverted(seedFailedUuid!, trimmedTitle)}
            data-testid="paste-convert-open-recipe"
          >
            Open the recipe
          </button>
        ) : (
          <button
            type="button"
            style={primaryButtonStyle(!canConvert)}
            onClick={() => void handleConvert()}
            disabled={!canConvert}
            data-testid="paste-convert-submit"
          >
            {submitting ? "Converting…" : "Convert"}
          </button>
        )}
      </div>
    </div>
  );
}

// ── Error matching ───────────────────────────────────────────────────────────

const FALLBACK_CLAIMS: PasteClaims = {
  aiOps: { used: 0, cap: 0, resetsAt: 0 },
  isPaid: false,
  optedIn: false,
  // `enabled: true` is deliberate. This fallback is only reached on an
  // `assistant_not_enabled` refusal, which the server raises ONLY after the kill
  // switch has passed — so the environment is on and the opted-out line is the
  // honest one. Seeding `false` here would show the unavailable line instead
  // (unavailable outranks opted-out) and point the user away from the Settings
  // toggle that actually unblocks them.
  enabled: true,
};

type PasteErrorMatch =
  | "assistant_not_enabled"
  | "assistant_unavailable"
  | "ai_gateway_unconfigured"
  | "ai_timeout"
  | "AI_OPS_EXCEEDED"
  | "paste_too_large"
  | "paste_convert_failed"
  | "paste_convert_seed_failed"
  | "unknown";

function readStr(obj: unknown, key: string): string {
  if (typeof obj !== "object" || obj === null) return "";
  const v = (obj as Record<string, unknown>)[key];
  return typeof v === "string" ? v : "";
}

function errorData(err: unknown): unknown {
  return typeof err === "object" && err !== null
    ? (err as Record<string, unknown>)["data"]
    : undefined;
}

/** Probe the thrown error for a paste-convert code (message / code / cause / data). */
function matchPasteError(err: unknown): PasteErrorMatch {
  const cause =
    typeof err === "object" && err !== null ? (err as Record<string, unknown>)["cause"] : undefined;
  const hay = [
    // Probe the STRUCTURED `data.code` first — that is what the server emits
    // (paste-convert.ts: `data: { code: err.code }`). `readStr(err, "code")` is
    // the oRPC transport code ("INTERNAL_SERVER_ERROR"), not our app code, and the
    // message only coincidentally carries the code today; `data.code` is the
    // reliable field. `data.error` is kept as a harmless legacy fallback.
    readStr(errorData(err), "code"),
    readStr(err, "code"),
    readStr(err, "message"),
    readStr(cause, "message"),
    readStr(errorData(err), "error"),
  ]
    .join(" ")
    .toLowerCase();

  // seed-failed is a MORE specific variant of paste_convert_failed — probe first.
  if (hay.includes("paste_convert_seed_failed")) return "paste_convert_seed_failed";
  if (hay.includes("assistant_not_enabled")) return "assistant_not_enabled";
  if (hay.includes("assistant_unavailable")) return "assistant_unavailable";
  if (hay.includes("ai_gateway_unconfigured")) return "ai_gateway_unconfigured";
  if (hay.includes("ai_timeout")) return "ai_timeout";
  if (hay.includes("ai_ops_exceeded")) return "AI_OPS_EXCEEDED";
  if (hay.includes("paste_too_large")) return "paste_too_large";
  if (hay.includes("paste_convert_failed")) return "paste_convert_failed";
  return "unknown";
}

/** Extract the created recipe UUID from a paste_convert_seed_failed error. */
function readSeedFailedUuid(err: unknown): string | null {
  const uuid = readStr(errorData(err), "recipeUuid");
  return uuid.length > 0 ? uuid : null;
}
